import { cn } from "@/utils/cn";
import { memo, useEffect, useRef, useState } from "react";

interface AnimatedProgressBarProps {
  targetProgress: number;
  classNames?: {
    container?: string;
    progressBar?: string;
    progressBarFill?: string;
  };
  animationDuration?: number;
}

export const AnimatedProgressBar = memo(function AnimatedProgressBar({
  targetProgress,
  classNames,
  animationDuration = 1000,
}: AnimatedProgressBarProps) {
  const [currentProgress, setCurrentProgress] = useState(0);
  const progressRef = useRef(0);

  useEffect(() => {
    const startProgress = progressRef.current;
    const progressDiff = targetProgress - startProgress;
    const intervalTime = 16; // ~60fps
    const steps = animationDuration / intervalTime;
    const increment = progressDiff / steps;

    let stepCount = 0;

    const interval = setInterval(() => {
      stepCount++;

      if (stepCount >= steps) {
        setCurrentProgress(targetProgress);
        progressRef.current = targetProgress;
        clearInterval(interval);
      } else {
        setCurrentProgress((prev) => {
          const next = prev + increment;
          // Ensure we don't overshoot
          const newProgress =
            increment > 0
              ? Math.min(next, targetProgress)
              : Math.max(next, targetProgress);
          progressRef.current = newProgress;
          return newProgress;
        });
      }
    }, intervalTime);

    return () => clearInterval(interval);
  }, [targetProgress, animationDuration]);

  return (
    <div
      className={cn("flex items-center justify-center", classNames?.container)}
    >
      <div
        className={cn(
          "w-[129px] h-[8px] rounded-full",
          "relative border border-white overflow-hidden bg-white",
          classNames?.progressBar,
        )}
      >
        <div
          className={cn(
            "absolute top-0 left-0 h-full",
            "bg-os-brand rounded-full transition-none",
            classNames?.progressBarFill,
          )}
          style={{ width: `${currentProgress}%` }}
        />
      </div>
    </div>
  );
});
